/**
 * Inline "unreachable" state — rendered in place of a spinner when the app
 * cannot reach the API (transport failure: offline, DNS):
 * "Can't reach Ving. Check your internet connection, then retry."
 *
 * A timeout with a healthy probe (the controller's `slow` state) renders
 * nothing here: the waiting region keeps waiting and shows Try again at 15 s
 * (#1561 W4, Q-582).
 *
 * Used by LoadingState (D4) and ProtectedRoute (#447). Not rendered
 * per-component; it is wired through the shared loading plumbing.
 */
import { testIds } from '@/test/testIds';
import { formMessages } from '@/constants';
import { clearUnreachable, triggerRecoveryProbe } from '@/connectivity';
import { Text } from '@/components/primitives';
import { RegionTryAgain, quietFailureStack } from '@/components/feedback/RegionLoadState/RegionTryAgain';

export interface UnreachableStateProps {
  /**
   * #447: optional override message. When provided, replaces the kind-derived
   * copy entirely. Used by ProtectedRoute to show failure-class-specific copy.
   */
  message?: string;
  /**
   * #447: additional callback fired AFTER the default clearUnreachable +
   * triggerRecoveryProbe. Used by ProtectedRoute to also invalidate AUTH_ME_KEY.
   */
  onRetry?: () => void;
  /** Another word on the quiet control. Only ProtectedRoute passes it (Q-656 item 3: "Retry"). */
  retryLabel?: string;
}

function defaultRetry() {
  clearUnreachable();
  triggerRecoveryProbe();
}

export function UnreachableState({ message, onRetry, retryLabel }: UnreachableStateProps) {
  const retry = () => { defaultRetry(); onRetry?.(); };
  return (
    <div
      data-testid={testIds.ui.unreachableState.root}
      style={{
        ...quietFailureStack,
        flex: 1,
      }}
    >
      <Text color="secondary" style={{ textAlign: 'center' }}>
        {message ?? formMessages.connectivity.unreachable}
      </Text>
      <RegionTryAgain testId={testIds.ui.unreachableState.retry} onRetry={retry} label={retryLabel} />
    </div>
  );
}
